import React, { useCallback, useState } from "react";
import { useAppRouteParams } from "../../../AppRoutes";
import {
  SaveType,
  postFileVersionDataExcel,
  postFileVersionDataJSON,
} from "../../../api/files";
import { getAPIErrorDetailOrMessage } from "../../../api/helpers";
import { useAppNotificationManager } from "../../AppProvider/AppNotificationProvider";
import { DialogPhaseType } from "../../Dialog/helpers/hooks";
import { DragAndDropUploadData } from "../../DragAndDropFields";

/**
 * @notice
 * このフックは、JSONとExcelのアップロード処理を共通化するために作成されています。
 * 将来、アップロードタイプごとに大きく処理が変わる場合は、以下の手順でコンポジションを解除できます:
 * 1. 各タイプ固有のフックファイル（useUploadSheetDataJSON.tsx など）に直接ロジックをコピーする
 * 2. このファイルの実装を参考にしながら、タイプごとに必要な処理を追加/変更する
 * 3. 各フックファイルからこのファイルへの依存を削除する
 * 4. 必要に応じてこのファイルを削除する
 */

type UploadFunction =
  | typeof postFileVersionDataExcel
  | typeof postFileVersionDataJSON;

/**
 * @description データアップロード用の共通ロジック
 * @param uploadedFileList アップロードされたファイルリスト
 * @param setUploadedFileList ファイルリスト更新関数
 * @param uploadDialogPhaseType アップロードダイアログのフェーズ
 * @param setUploadDialogPhaseType アップロードダイアログのフェーズを設定する関数
 * @param saveType 保存タイプ
 * @param uploadFunction ファイルタイプ別のアップロード関数
 */
export const useUploadSheetData = (
  uploadedFileList: DragAndDropUploadData[],
  setUploadedFileList: React.Dispatch<
    React.SetStateAction<DragAndDropUploadData[]>
  >,
  uploadDialogPhaseType: DialogPhaseType,
  setUploadDialogPhaseType: (uploadDialogPhaseType: DialogPhaseType) => void,
  saveType: SaveType,
  uploadFunction: UploadFunction
) => {
  const { organizationId, versionId } = useAppRouteParams();

  const [isUploading, setIsUploading] = useState(false);

  const { showErrorNotification, showSuccessNotification } =
    useAppNotificationManager();

  // APIによる生成実行可否
  const isEnableGenerateSheetData = uploadedFileList.length === 1;

  const runUpload = useCallback(async () => {
    setIsUploading(true);
    setUploadedFileList((prev) =>
      prev.map((item) => ({ ...item, isDisableDelete: true }))
    );
    try {
      const file = uploadedFileList[0].file;
      await uploadFunction({
        organizationId,
        versionId,
        file,
        saveType,
      });
      showSuccessNotification("シートデータのアップロードに成功しました");
    } catch (error) {
      const { errorMessage } = getAPIErrorDetailOrMessage(error);
      showErrorNotification(errorMessage);
    } finally {
      setUploadDialogPhaseType(null);
      setUploadedFileList([]);
      setIsUploading(false);
    }
  }, [uploadedFileList, saveType, uploadFunction, organizationId, versionId]);

  const isDisablePrimaryButton = !isEnableGenerateSheetData || isUploading;

  const isDisableCancelButton = isUploading;

  return {
    runUpload,
    isDisablePrimaryButton,
    isDisableCancelButton,
  };
};
